import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { formatTimeAgoShort } from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import * as Icon from '../../components/icons';
import { Stat } from '../../components/stats/Stat';
import { AgentCategoryId } from '../../resourcePolicy/agentData/agent.types';
import { getAgentIcon } from '../../resourcePolicy/agentData/agentDataHelper';
import { useActualityAggregatedStats } from '../hooks/useActualityAggregatedStats';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import useGetActualityAggregationGroupColors from '../hooks/useGetActualityAggregationGroupColors';
import useGetActualityAggregationGroupTitle from '../hooks/useGetActualityAggregationGroupTitle';
import { actualityAggregatedStatsSelectedAggregatedMetric } from '../store/actuality.kpi.state';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregationColorBy,
  ActualityAggregationField,
} from '../store/actuality.kpi.types';
import { actualityResourcePolicy } from '../store/actuality.state';
import { feedDistinctAgents } from '../store/feed.state';
import ActualityAggregatedStats, {
  ActualityAggregatedStatsProps,
} from './ActualityAggregatedStats';
// New event configuration system
import { useAtomValue } from 'jotai';
import { toHumanReadableTime } from '../../common/dateTimeHelper';
import {
  EVENT_GROUPING_CONFIG,
  EVENT_STATS_CONFIG,
  detectGroupingFromKeyFields,
  getGroupingIcon,
} from '../constants/actuality.event.config';
import { getAggregatedStatsKey } from '../store/actuality.kpi.helper';
import { actualityAggregatedStatsAllSelectedWhenEmpty } from '../store/actuality.kpi.ui.state';
import {
  getConfigForContext,
  getPrimaryStatsForGrouping,
} from '../utils/eventConfigUtils';
import ActualityEntityTag from './ActualityIEntityTag';

export type ActualityAggregatedStatsItemProps =
  ActualityAggregatedStatsProps & {
    keyFields: ActualityAggregationField[];
    colorFields?: ActualityAggregationField[];
    supportedColorBy?: ActualityAggregationColorBy[];
    selected: Partial<ActualityAggregatedHqExtendedDataRow>[];
    onSelectionChanged: (
      keys: Partial<ActualityAggregatedHqExtendedDataRow>[],
    ) => void;
    className?: string;
    children?: React.ReactNode;
    displayMode?: 'summary' | 'details' | 'process' | 'events';
  };

const ActualityAggregatedStatsItem: React.FC<
  ActualityAggregatedStatsItemProps
> = props => {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const aggMetric = useRecoilValue(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );

  const metricDescriptors = useActualityAggregationMetricDescriptors();
  const resourcePolicy = useRecoilValue(actualityResourcePolicy);
  const agentMapByResourcePolicy = _.keyBy(resourcePolicy.agents, 'id');
  const feedAgents = useRecoilValue(feedDistinctAgents);
  const feedAgentsByUser = _.keyBy(feedAgents, 'agentUser');
  const feedAgentsByType = _.keyBy(feedAgents, 'agentType');
  const aggFieldsDescriptors = useActualityAggregationFieldDescriptors();
  const getGroupTitle = useGetActualityAggregationGroupTitle();
  const getGroupColors = useGetActualityAggregationGroupColors();
  const groupTitle = getGroupTitle(props.data, props.keyFields);
  const selectAllWhenNoExplicitSelection = useAtomValue(
    actualityAggregatedStatsAllSelectedWhenEmpty,
  );

  // Function to get title for any field (from metric descriptors or event config)
  const getFieldTitle = (fieldKey: string): string => {
    // First try metric descriptors (for aggregated stats)
    if (metricDescriptors[fieldKey as keyof typeof metricDescriptors]) {
      return (
        metricDescriptors[fieldKey as keyof typeof metricDescriptors].title ||
        fieldKey
      );
    }

    // Then try event stats config (for event fields like eventHour)
    const eventStat = EVENT_STATS_CONFIG.find(stat => stat.key === fieldKey);
    if (eventStat) {
      return eventStat.title(t);
    }

    // Fallback to formatted field name
    return fieldKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  const {
    showAll,
    setShowAll,
    shouldShowSummaryToggle,
    getTotalAvailableFields,
    fieldsToShowByCategory,
    hasCategoryStats,
  } = useActualityAggregatedStats(props);

  const colors = getGroupColors(
    props.data,
    props.colorFields,
    props.supportedColorBy,
  );

  const interval = props.data.totalEmployeeActivityDuration
    ? formatter.formatTimespanAs24String(
        props.data.totalEmployeeActivityDuration,
      )?.fullString
    : '';

  const metricValue = _.get(props.data, aggMetric);

  const metricValueFormatted = metricDescriptors[aggMetric]?.getFormatted(
    metricValue,
  ) || {
    value: 'N/A',
    unit: 'N/A',
  };

  const hasExplicitlySelected = !_.isEmpty(props.selected);
  const selectionKey = getAggregatedStatsKey(props.data, props.keyFields);
  const isSelectedExplicitly = _.some(props.selected, g =>
    _.isEqual(g, selectionKey),
  );

  const isSelected =
    isSelectedExplicitly ||
    (selectAllWhenNoExplicitSelection && !hasExplicitlySelected);

  const others = _.filter(props.selected, key => !_.isEqual(key, selectionKey));

  // NEW: Use global configuration-based grouping detection
  const detectedGrouping = detectGroupingFromKeyFields(props.keyFields);
  const groupingConfig = EVENT_GROUPING_CONFIG[detectedGrouping];

  // Replace legacy checks with config-based detection
  const isAgentGrouping = detectedGrouping === 'agent';
  const isLocationGrouping = detectedGrouping === 'location';
  const isItemGrouping = detectedGrouping === 'item';
  const isProcessGrouping = detectedGrouping === 'process';
  const isTimeGrouping = detectedGrouping === 'time';

  // NEW: Get icon based on detected grouping
  const getDisplayIcon = () => {
    const iconName = getGroupingIcon(detectedGrouping);

    // Check if this is any agent-related grouping
    const isAgentRelatedGrouping =
      detectedGrouping === 'agent' ||
      detectedGrouping === 'agentId' ||
      props.keyFields.some(field =>
        ['agentId', 'agentType', 'agentUser'].includes(field),
      );

    // Special handling for all agent-related groupings
    if (isAgentRelatedGrouping) {
      // Try to get agentType from the data - it could be under different field names
      let agentType = props.data.agentType || props.data.agentTypes?.[0];

      // If we don't have agentType directly, try to derive it from agentUser or agentId
      if (!agentType) {
        const agentUser = props.data.agentUser;
        const agentId = props.data.agentId;

        if (agentUser && feedAgentsByUser[agentUser]) {
          agentType = feedAgentsByUser[agentUser].agentType;
        } else if (agentId && feedAgents.find(a => a.agentId === agentId)) {
          agentType = feedAgents.find(a => a.agentId === agentId)?.agentType;
        }
      }

      if (agentType) {
        const resourceTypeId = agentMapByResourcePolicy[agentType]
          ?.resourceTypeId as AgentCategoryId;
        const agentIcon = getAgentIcon(resourceTypeId);
        if (agentIcon) {
          return agentIcon;
        }
      }

      // Final fallback: try to find agent directly in resource policy
      const agentIdentifier =
        props.data.agentId || props.data.agentUser || props.data.agentType;
      if (agentIdentifier) {
        const agent = resourcePolicy.agents.find(
          a =>
            a.id === agentIdentifier ||
            a.title === agentIdentifier ||
            a.id === agentType,
        );
        if (agent?.resourceTypeId) {
          const agentIcon = getAgentIcon(
            agent.resourceTypeId as AgentCategoryId,
          );
          if (agentIcon) {
            return agentIcon;
          }
        }
      }
    }

    // Use the configured icon from EVENT_GROUPING_CONFIG
    return Icon[iconName as keyof typeof Icon] || Icon.List;
  };

  const displayIcon = getDisplayIcon();

  // NEW: Use configuration-based stats field selection
  const getStatsFieldsForGrouping = () => {
    // Try using the new configuration system first
    const configuredStats = getPrimaryStatsForGrouping(detectedGrouping);

    // Map configuration field names to actual data field names
    const mappedFields = configuredStats
      .map(fieldKey => {
        // Map event config field names to aggregated data field names
        switch (fieldKey) {
          case 'duration':
            return 'analyzedDuration';
          case 'distance':
            return 'analyzedDistance';
          case 'agentType':
            return 'agentTypes';
          case 'eventProcessType':
            return 'processTypes';
          case 'locationId':
            return 'locationIds';
          default:
            return fieldKey;
        }
      })
      .filter(
        field =>
          props.data.hasOwnProperty(field) || field === 'analyzedDuration',
      );

    // Fallback to legacy logic if no mapped fields found
    if (mappedFields.length === 0) {
      // Legacy fallback logic
      // if (isAgentIdGrouping) {
      //   return ['analyzedDuration'] as any;
      // }
      if (isAgentGrouping) {
        return [
          'analyzedDuration',
          'analyzedDistance',
          'durationDiff',
          'durationRate',
          'analyzedCost',
          'analyzedCostTravellingStart',
          'analyzedCostTravellingEnd',
          'analyzedCostTravellingInternal',
          'analyzedCostTravellingVertical',
          'analyzedCostHandling',
        ] as any;
      }
      if (isLocationGrouping) {
        return ['analyzedDuration'] as any;
      }
      if (isItemGrouping) {
        return [
          'quantity',
          'totalWeight',
          'totalVolume',
          'analyzedDuration',
          'jobId',
          'jobGroupId',
          'eventProcessType',
          'eventType',
        ] as any;
      }
      if (isProcessGrouping) {
        return ['analyzedDuration', 'analyzedDistance'] as any;
      }
      if (isTimeGrouping) {
        return ['eventsCount', 'analyzedDuration'] as any;
      }
      return (
        props.summaryFields || (['analyzedDuration', 'analyzedDistance'] as any)
      );
    }

    return mappedFields as any;
  };

  // NEW: Get display configuration based on context
  const displayConfig = getConfigForContext(
    props.displayMode === 'summary'
      ? 'card'
      : props.displayMode === 'details'
        ? 'detail'
        : 'list',
  );

  function onClick() {
    const newSelected = isSelectedExplicitly
      ? others
      : [...others, selectionKey];
    props.onSelectionChanged(newSelected);
  }

  // Unified color mapping for display modes
  const colorByDisplayMode: Record<string, [string, string] | null> = {
    process: colors,
    summary: colors,
    events: null,
    details: null,
  };

  const colorLegendByDisplayMode: Record<string, [string, string] | null> = {
    process: colors,
    summary: colors,
    events: null,
    details: null,
  };

  const colorActiveByDisplayMode: Record<string, [string, string] | null> = {
    process: colors,
    summary: colors,
    events: colors,
    details: null,
  };

  const colorHoverByDisplayMode: Record<string, [string, string] | null> = {
    process: colors,
    summary: colors,
    events: colors,
    details: null,
  };

  const statColor = colorByDisplayMode[props.displayMode ?? ''] ?? null;
  const statLegendColor =
    colorLegendByDisplayMode[props.displayMode ?? ''] ?? null;
  const statColorHover =
    colorHoverByDisplayMode[props.displayMode ?? ''] ?? null;
  const statColorActive =
    colorActiveByDisplayMode[props.displayMode ?? ''] ?? null;

  return (
    <Stat
      dataComponent={`ActualityAggregatedStatsItem-${props.displayMode}`}
      // inPanelMode
      // hasHelper
      // isPreview
      isActionable
      className={props.className}
      legendColor={statLegendColor}
      color={statColor}
      colorHover={statColorHover}
      colorActive={statColorActive}
      isSelected={isSelected}
      icon={
        props.displayMode === 'summary' || props.displayMode === 'process'
          ? displayIcon
          : null
      }
      titleElement={
        <div className={'flex w-full flex-col items-start'}>
          {/* Show matching fields when searching */}
          {/* {props.matchingFields && props.matchingFields.length > 0 && (
            <div className="border-menu/20 bg-app-panel-dark/80 text-menu-text text-xxs mb-2 mt-1 w-full rounded border px-2 py-1">
              <span className="uppercase">{t('Matched:')}</span>{' '}
              {props.matchingFields.join(', ')}
            </div>
          )} */}

          {/* {props.displayMode === 'events'
            ? (props.data?.eventEndTime?.toLocaleString() ?? '')
            : groupTitle} */}

          {/* {props.displayMode} */}
          {/* {groupTitle} */}
          {props.data.minEventTime && (
            <div
              title={`Start: ${props.data.minEventTime.toLocaleString()} - End: ${props.data.maxEventTime.toLocaleString()}`}
              className="mb-0.5 flex w-full items-center gap-1 truncate text-xs"
            >
              <div className="flex-1 text-xs">
                {props.displayMode === 'details'
                  ? toHumanReadableTime(props.data.minEventTime)
                  : props.displayMode === 'events'
                    ? toHumanReadableTime(props.data.minEventTime)
                    : props.displayMode === 'process' ||
                        props.displayMode === 'summary'
                      ? formatTimeAgoShort(props.data.minEventTime)
                      : props.data.minEventTime.toLocaleString()}
              </div>

              {props.data[props.keyFields[1]] && (
                <div
                  data-component="ActualityAggregatedStatsItem-subGroup"
                  className="border-menu/10 flex flex-col items-end justify-end gap-1 truncate rounded-md border px-2 py-0.5 opacity-60"
                >
                  <div className="text-xxs truncate">
                    {props.data[props.keyFields[1]] instanceof Date
                      ? props.data[props.keyFields[1]].toLocaleString()
                      : String(props.data[props.keyFields[1]])}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* <div
            title={t(`Net Time: {{netTime}}`, {
              netTime: interval,
            })}
            className={'text-xxs opacity-75'}
          >
            {interval}
          </div> */}
          {/* <span className={'flex-1'}>{groupTitle}</span> */}
          <ActualityEntityTag
            className="roundet-t-none bg-transparent text-xs font-bold backdrop-blur-sm"
            entity="agentId"
            title={
              // groupBy
              props.data[props.keyFields[0]] instanceof Date
                ? props.data[props.keyFields[0]].toLocaleString()
                : String(props.data[props.keyFields[0]])
            }
            filters={{
              [props.keyFields[0]]:
                props.data[props.keyFields[0]] instanceof Date
                  ? props.data[props.keyFields[0]].toString()
                  : String(props.data[props.keyFields[0]]),
            }}
            iconDropMenu={
              props.displayMode === 'events'
                ? displayIcon
                : props.displayMode === 'summary' ||
                    props.displayMode === 'process'
                  ? undefined
                  : displayIcon
            }
          />
        </div>
      }
      //classIcon={cn('w-9 h-9 xl:w-12 xl:h-12')}
      value={metricValueFormatted.value}
      unitOfMeasure={metricValueFormatted.unit}
      onClick={onClick}
      subStats={
        <>
          <ActualityAggregatedStats
            data={props.data}
            mode="fieldsOnly"
            fields={getStatsFieldsForGrouping()}
            shouldShowSummaryToggle
          />

          {/* TODO: Add summary toggle */}
          {/* 
          <SummaryToggle
            shouldShow={shouldShowSummaryToggle}
            showAll={showAll}
            count={getTotalAvailableFields()}
            onToggle={setShowAll}
            titleShow={
              props.displayMode === 'events' ? `Show Events` : undefined
            }
            titleHide={
              props.displayMode === 'events' ? `Hide Events` : undefined
            }
            className={classNames(
              'mt-2',
              'w-full',
              'opacity-0 group-hover/stat:opacity-100',
              'absolute bottom-0 left-0',
            )}
          /> */}

          {/* Debug info - show detected grouping in development */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div className="text-xs opacity-50 mt-1">
              Detected: {detectedGrouping} | Icon: {groupingConfig.icon}
            </div>
          )} */}
        </>
      }
      toggleAction={
        <>
          {/* {props.displayMode === 'events' && (
            <div className="hidden pt-4 group-hover/stat:block">
              <SummaryToggle
                shouldShow={shouldShowSummaryToggle}
                showAll={showAll}
                count={getTotalAvailableFields()}
                onToggle={setShowAll}
                titleShow="Show Event Details"
                titleHide="Hide Event Details"
                // className="absolute bottom-2 left-0"
              />
            </div>
          )} 
           */}
        </>
      }
      hasEvents={<>{props.children}</>}
    />
  );
};

export default ActualityAggregatedStatsItem;
